import { dayOf, handled, type Route, type Stop } from '@day';
import { phoneMatches, streetOf } from '../app/words';

/**
 * The Routes page on Routr Dispatch (board `routes-web`): a day's routes in sections, and "Find a stop in any route".
 * Kept apart from the page so it can be tested.
 */
export type RoutesSec = 'on' | 'waiting' | 'finished' | 'notFinished';

export const SECTIONS: [RoutesSec, string][] = [
  ['on', 'On a route'],
  ['waiting', 'Waiting to start'],
  ['finished', 'Finished'],
  ['notFinished', 'Not finished'],
];

/** Where a route goes: one that ended with stops still to do, or was declined, isn't finished. */
export function sectionOf(r: Pick<Route, 'state' | 'stops'>): RoutesSec {
  if (r.state === 'running') return 'on';
  if (r.state === 'waiting') return 'waiting';
  if (r.state === 'declined') return 'notFinished';
  return r.stops.every(handled) ? 'finished' : 'notFinished';
}

/** The business's day a past route is listed under: the day it started. */
export const routeDay = (r: Route, zone: string) => dayOf(r.startedAt ?? r.completedAt ?? r.endedAt ?? 0, zone);

/** A stop found by its street, its address, the customer, their phone (however it was written) or a note. */
export function stopMatches(s: Stop, q: string, country: string | null): boolean {
  const ql = q.trim().toLowerCase();
  if (!ql) return false;
  return [s.street, s.address, s.name, s.phone, s.notes, s.access, s.courierNote].join(' ').toLowerCase().includes(ql) || phoneMatches(q.trim(), s.phone, country);
}

export interface RoutesRow {
  r: Route;
  /** The route's stops the search found; none without a search. */
  hits: Stop[];
}

export interface RoutesSection {
  id: RoutesSec;
  label: string;
  rows: RoutesRow[];
}

/** When a route last moved, for its place in its section: the newest ended first, the oldest started or sent first. */
const when = (r: Route) => r.completedAt ?? r.endedAt ?? r.declinedAt ?? r.startedAt ?? r.sentAt ?? 0;

/**
 * The page's sections, those with nothing in them left out. With a search, only the routes with a stop it finds, each
 * with those stops.
 */
export function routesSections(routes: Route[], q: string, country: string | null): RoutesSection[] {
  const searching = q.trim().length > 0;
  const seen = new Set<string>();
  const rows: RoutesRow[] = [];
  for (const r of routes) {
    if (seen.has(r.id)) continue;
    seen.add(r.id);
    const hits = searching ? r.stops.filter((s) => stopMatches(s, q, country)) : [];
    if (!searching || hits.length) rows.push({ r, hits });
  }
  return SECTIONS.map(([id, label]) => {
    const late = id === 'finished' || id === 'notFinished';
    const mine = rows.filter((x) => sectionOf(x.r) === id).sort((a, b) => (late ? when(b.r) - when(a.r) : when(a.r) - when(b.r)));
    return { id, label, rows: mine };
  }).filter((x) => x.rows.length > 0);
}

export interface PastHit {
  r: Route;
  stop: Stop;
  day: string;
}

/**
 * Stops on past days' routes a search finds (⌘K, board `search-past`), the newest first, at most [cap] of [total]. A past
 * route's record keeps streets and addresses only, so that's what's searched.
 */
export function pastStops(history: Route[] | null, q: string, today: string, zone: string, cap = 6): { hits: PastHit[]; total: number } {
  const ql = q.trim().toLowerCase();
  if (ql.length < 2 || !history) return { hits: [], total: 0 };
  const hits: PastHit[] = [];
  for (const r of history) {
    const day = routeDay(r, zone);
    if (day >= today) continue;
    for (const stop of r.stops) {
      if ([streetOf(stop), stop.address, stop.writtenAddress].join(' ').toLowerCase().includes(ql)) hits.push({ r, stop, day });
    }
  }
  hits.sort((a, b) => b.day.localeCompare(a.day) || when(b.r) - when(a.r) || a.stop.n - b.stop.n);
  return { hits: hits.slice(0, cap), total: hits.length };
}
